import { inject, Injectable } from '@angular/core';
import {
  catchError,
  defer,
  defaultIfEmpty,
  EMPTY,
  shareReplay,
  takeUntil,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import {
  ObjectTools,
  SolFieldKeyword,
  SolPreferenceStore,
  SolSettingKey,
  SolTableLayout,
  SolTableSort,
  SolVisibleColumn,
  TableTools,
} from '@sol/components';
import { AbstractAsmService } from '@asm/components-core/services/abstract-asm.service';
import {
  AsmResolvedLayout,
  AsmSettingContext,
  AsmSettingDefinition,
  AsmSettingDefinitionId,
  AsmSettingPayloadRow,
  AsmSettingValue,
  AsmTableColumnWidths,
} from '@asm/components-core/models/asm-setting';
import { AsmTableColumnWidthsTools } from '@asm/components-table/tools/asm-table-column-widths.tools';
import { AsmAppContext, SessionService } from './session.service';
import { AsmUiStateService } from './asm-ui-state.service';
import { AsmResolvedSettingsDto, AsmSettingsAdminService } from './asm-settings-admin.service';

interface AsmSettingAddress {
  definition: string;
  name: string;
}

export class AsmSettingsState {
  private constructor(
    public readonly layouts: Record<string, AsmResolvedLayout>,
    public readonly writtenKeys: string[],
    public readonly context: AsmAppContext | undefined,
    public readonly writeVersions: Record<string, number>,
  ) {}

  public static of(): AsmSettingsState {
    return new AsmSettingsState({}, [], undefined, {});
  }

  public withWriteAttempt(key: string): AsmSettingsState {
    return this.copy({
      writtenKeys: this.writtenKeys.includes(key) ? this.writtenKeys : [...this.writtenKeys, key],
      writeVersions: { ...this.writeVersions, [key]: (this.writeVersions[key] ?? 0) + 1 },
    });
  }

  public withContext(context: AsmAppContext | undefined): AsmSettingsState {
    return this.copy({
      context,
      layouts: {},
      writeVersions: this.context === undefined && context !== undefined ? this.writeVersions : {},
      writtenKeys: this.context === undefined && context !== undefined ? this.writtenKeys : [],
    });
  }

  public withLayout(
    context: AsmAppContext,
    table: string,
    layout: AsmResolvedLayout | undefined,
    key: string,
    version: number,
  ): AsmSettingsState {
    if (!ObjectTools.equals(this.context, context) || (this.writeVersions[key] ?? 0) !== version) return this;
    const layouts = { ...this.layouts };
    if (layout === undefined) delete layouts[table];
    else layouts[table] = layout;
    return this.copy({ layouts });
  }

  private copy(props: Partial<AsmSettingsState>): AsmSettingsState {
    return ObjectTools.copy<AsmSettingsState>(this, props);
  }
}

/** Adapts the flat `SolPreferenceStore` key space onto the settings contract's definitions and fields. */
@Injectable({
  providedIn: 'root',
})
export class AsmSettingsService extends AbstractAsmService<AsmSettingsState> implements SolPreferenceStore {
  private static readonly UI_KEY_FIELDS: Record<string, string> = {
    [SolSettingKey.theme.name]: 'theme',
    [SolSettingKey.locale.name]: 'locale',
    [SolSettingKey.navbarVisible.name]: 'navbar_visible',
    [SolSettingKey.detailFixed.name]: 'detail_fixed',
    [SolSettingKey.detailWidth.name]: 'detail_width',
  };
  /** The keys painted before the app boots, mirroring `PRE_PAINT_FIELDS` in the backend resolver. */
  private static readonly PRE_PAINT_KEYS: readonly string[] = [SolSettingKey.theme.name, SolSettingKey.locale.name];
  private static readonly COLUMNS_KEY_PREFIX = 'asm:table:';
  private static readonly COLUMNS_KEY_SUFFIX = ':columns';
  private static readonly ENTRY_KEY_TABLE = 'table';
  private static readonly FIELD_COLUMNS = 'columns';
  private static readonly FIELD_HIDDEN = 'hidden';
  private static readonly FIELD_COLUMN_WIDTHS = 'column_widths';
  private static readonly FIELD_SORT_COLUMN = 'sort_column';
  private static readonly FIELD_SORT_DIRECTION = 'sort_direction';

  // Dependencies
  private readonly sessionService = inject(SessionService);
  private readonly adminService = inject(AsmSettingsAdminService);
  private readonly uiStateService = inject(AsmUiStateService);

  // Cache
  private readonly writes: Record<string, Observable<void>> = {};
  private requests: Record<string, Observable<unknown>> = {};
  private readonly reportedKeys = new Set<string>();

  constructor() {
    super('/api/v3/user/settings', AsmSettingsState.of());
    this.contextChanges().subscribe(context => {
      this.requests = {};
      this.update(state => state.withContext(context));
    });
  }

  public contextChanges(): Observable<AsmAppContext | undefined> {
    return this.sessionService.whenContextReady();
  }

  public load(key: string): Observable<unknown> {
    const address = AsmSettingsService.toAddress(key);
    if (address?.definition === AsmSettingDefinitionId.tableLayout) {
      return this.contextChanges().pipe(
        switchMap(context => (context === undefined ? EMPTY : this.readTable(context, address.name))),
      );
    }
    return defer(() => {
      const version = this.state.writeVersions[key] ?? 0;
      return this.whenResolved().pipe(
        take(1),
        filter(() => (this.state.writeVersions[key] ?? 0) === version),
        map(() => this.read(key)),
      );
    });
  }

  public save(key: string, value: unknown): Observable<void> {
    const context = this.state.context;
    return this.enqueue(key, () =>
      !ObjectTools.equals(context?.userId, this.state.context?.userId)
        ? throwError(() => new Error('The authenticated user changed before this setting could be saved.'))
        : this.write(key, value),
    );
  }

  public reset(key: string): Observable<void> {
    const context = this.state.context;
    return this.enqueue(key, () =>
      !ObjectTools.equals(context?.userId, this.state.context?.userId)
        ? throwError(() => new Error('The authenticated user changed before this setting could be cleared.'))
        : this.discard(key),
    );
  }

  /** Never completes: a scope or app switch re-emits, so `lastValueFrom` on this hangs forever. */
  public prePaint(): Observable<Record<string, unknown>> {
    return this.whenResolved().pipe(
      map(() => this.toPrePaint(key => this.read(key))),
      distinctUntilChanged((previous, current) => ObjectTools.equals(previous, current)),
    );
  }

  /** The key one table's layout is stored under, carrying how a stored value narrows back to a layout. */
  public tableKey(tableId: string): SolSettingKey<SolTableLayout> {
    return SolSettingKey.of(
      `${AsmSettingsService.COLUMNS_KEY_PREFIX}${tableId}${AsmSettingsService.COLUMNS_KEY_SUFFIX}`,
      TableTools.toStoredLayout,
    );
  }

  /** Whether the caller's own rule holds the layout: one resolved from a level above is not theirs to clear. */
  public ownsTableLayout(tableId: string): boolean {
    return this.toLayout(tableId)?.context === AsmSettingContext.user;
  }

  public keywords(key: string): Observable<readonly SolFieldKeyword[]> {
    const address = AsmSettingsService.toAddress(key);
    if (address === undefined) {
      return of([]);
    }
    return this.adminService
      .whenDefinitionsFetched()
      .pipe(map(definitions => AsmSettingsService.toKeywords(definitions, address)));
  }

  private read(key: string): unknown {
    const address = AsmSettingsService.toAddress(key);
    if (address === undefined) {
      this.reportUnaddressable(key);
      return undefined;
    }
    if (address.definition === AsmSettingDefinitionId.tableLayout) {
      const layout = this.toLayout(address.name);
      return layout === undefined ? undefined : AsmSettingsService.toTableLayout(layout.values);
    }
    return this.uiStateService.state.preferences[address.name];
  }

  private toLayout(tableId: string): AsmResolvedLayout | undefined {
    return this.state.layouts[tableId];
  }

  private write(key: string, value: unknown): Observable<void> {
    const address = AsmSettingsService.toAddress(key);
    if (address === undefined) {
      this.reportUnaddressable(key);
      return throwError(() => new Error(`Setting "${key}" addresses no settings definition, so it cannot be saved.`));
    }
    const context = this.state.context;
    this.update(state => state.withWriteAttempt(key));
    return this.httpPut<AsmSettingPayloadRow, void>(
      `/${address.definition}`,
      AsmSettingsService.toWriteBody(address, value),
    ).pipe(
      switchMap(() => (ObjectTools.equals(context, this.state.context) ? this.reresolve(address) : of(undefined))),
    );
  }

  private discard(key: string): Observable<void> {
    const address = AsmSettingsService.toAddress(key);
    if (address === undefined) {
      this.reportUnaddressable(key);
      return throwError(() => new Error(`Setting "${key}" addresses no settings definition, so it cannot be cleared.`));
    }
    const context = this.state.context;
    this.update(state => state.withWriteAttempt(key));
    const request =
      address.definition === AsmSettingDefinitionId.tableLayout
        ? this.httpDelete<void>(`/${address.definition}`, {
            params: { [AsmSettingsService.ENTRY_KEY_TABLE]: address.name },
          })
        : this.httpPut<AsmSettingPayloadRow, void>(`/${address.definition}`, { [address.name]: null });
    return request.pipe(
      switchMap(() => (ObjectTools.equals(context, this.state.context) ? this.reresolve(address) : of(undefined))),
    );
  }

  private reportUnaddressable(key: string): void {
    if (this.reportedKeys.has(key)) {
      return;
    }
    this.reportedKeys.add(key);
    console.error(`Setting "${key}" addresses no settings definition, so it will not be persisted.`);
  }

  private enqueue(key: string, request: () => Observable<void>): Observable<void> {
    const previous = this.writes[key];
    const outcome = new ReplaySubject<void>(1);
    (previous === undefined ? request() : previous.pipe(switchMap(request))).subscribe({
      next: value => outcome.next(value),
      error: (error: unknown) => outcome.error(error),
      complete: () => outcome.complete(),
    });
    this.writes[key] = outcome.pipe(catchError(() => of(undefined)));
    return outcome.asObservable();
  }

  private whenResolved(): Observable<AsmSettingsState> {
    return this.uiStateService.whenResolved().pipe(map(() => this.state));
  }

  /** Only a layout is read back after a write, and `detailWidth` would cost a round trip per drag frame. */
  private reresolve(address: AsmSettingAddress): Observable<void> {
    if (address.definition !== AsmSettingDefinitionId.tableLayout) {
      return of(undefined);
    }
    const context = this.state.context;
    if (context === undefined) return of(undefined);
    delete this.requests[address.name];
    return this.readTable(context, address.name).pipe(
      map(() => undefined),
      defaultIfEmpty(undefined),
      catchError(() => of(undefined)),
    );
  }

  private readTable(context: AsmAppContext, table: string): Observable<unknown> {
    const key = this.tableKey(table).name;
    const version = this.state.writeVersions[key] ?? 0;
    if (this.requests[table] === undefined) {
      const request = this.httpGet<AsmResolvedSettingsDto>('', {
        params: { ...context.params, definition: AsmSettingDefinitionId.tableLayout, key: table },
      }).pipe(
        takeUntil(this.contextChanges().pipe(filter(current => !ObjectTools.equals(current, context)))),
        map(dto => {
          const entry = AsmSettingsAdminService.toResolvedSettings(dto)[AsmSettingDefinitionId.tableLayout]?.entries[0];
          return entry === undefined ? undefined : { context: entry.context, values: entry.values };
        }),
        tap({
          next: layout => this.update(state => state.withLayout(context, table, layout, key, version)),
          error: () => {
            if (this.requests[table] === request) delete this.requests[table];
          },
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.requests[table] = request;
    }
    return this.requests[table].pipe(
      filter(() => ObjectTools.equals(this.state.context, context) && (this.state.writeVersions[key] ?? 0) === version),
      map(() => this.read(key)),
    );
  }

  /**
   * A resolved key carries its value, an unresolved one a `null` the shell reads as "apply the default".
   * A key the caller has just written is left out entirely, so re-resolving it cannot stomp their write.
   */
  private toPrePaint(resolve: (key: string) => unknown): Record<string, unknown> {
    const prePainted: Record<string, unknown> = {};
    for (const key of AsmSettingsService.PRE_PAINT_KEYS) {
      if (this.state.writtenKeys.includes(key)) {
        continue;
      }
      prePainted[key] = resolve(key) ?? null;
    }
    return prePainted;
  }

  private static toAddress(key: string): AsmSettingAddress | undefined {
    const field = AsmSettingsService.UI_KEY_FIELDS[key];
    if (field !== undefined) {
      return { definition: AsmSettingDefinitionId.uiPreferences, name: field };
    }
    const tableId = AsmSettingsService.toTableId(key);
    return tableId === undefined ? undefined : { definition: AsmSettingDefinitionId.tableLayout, name: tableId };
  }

  private static toTableId(key: string): string | undefined {
    if (
      !key.startsWith(AsmSettingsService.COLUMNS_KEY_PREFIX) ||
      !key.endsWith(AsmSettingsService.COLUMNS_KEY_SUFFIX)
    ) {
      return undefined;
    }
    const tableId = key.slice(
      AsmSettingsService.COLUMNS_KEY_PREFIX.length,
      key.length - AsmSettingsService.COLUMNS_KEY_SUFFIX.length,
    );
    return tableId.length === 0 ? undefined : tableId;
  }

  private static toKeywords(
    definitions: readonly AsmSettingDefinition[],
    address: AsmSettingAddress,
  ): readonly SolFieldKeyword[] {
    const field = definitions
      .find(definition => definition.id === address.definition)
      ?.fields.find(candidate => candidate.name === address.name);
    return field?.keywords ?? [];
  }

  private static toWriteBody(address: AsmSettingAddress, value: unknown): AsmSettingPayloadRow {
    if (address.definition !== AsmSettingDefinitionId.tableLayout) {
      return { [address.name]: value as AsmSettingValue };
    }
    const layout = TableTools.toStoredLayout(value);
    const columns = layout?.columns ?? [];
    return {
      [AsmSettingsService.ENTRY_KEY_TABLE]: address.name,
      [AsmSettingsService.FIELD_SORT_COLUMN]: layout?.sort?.columnId ?? null,
      [AsmSettingsService.FIELD_SORT_DIRECTION]: layout?.sort?.direction ?? null,
      [AsmSettingsService.FIELD_COLUMNS]: columns.map(column => column.id),
      [AsmSettingsService.FIELD_HIDDEN]: columns.filter(column => !column.visible).map(column => column.id),
      [AsmSettingsService.FIELD_COLUMN_WIDTHS]: AsmSettingsService.toColumnWidths(columns),
    };
  }

  /** Sent as `null` once no column is resized, because the write merges and would keep the old widths. */
  private static toColumnWidths(columns: readonly SolVisibleColumn[]): AsmTableColumnWidths | null {
    const widths = columns.flatMap(column => (column.width === undefined ? [] : [[column.id, column.width] as const]));
    return widths.length === 0 ? null : Object.fromEntries(widths);
  }

  private static toTableLayout(entry: AsmSettingPayloadRow): SolTableLayout | undefined {
    const columns = entry[AsmSettingsService.FIELD_COLUMNS];
    if (!Array.isArray(columns)) {
      return undefined;
    }
    const hidden = entry[AsmSettingsService.FIELD_HIDDEN];
    const hiddenIds = new Set(Array.isArray(hidden) ? hidden.map(id => String(id)) : []);
    const widths = AsmTableColumnWidthsTools.toWidths(entry[AsmSettingsService.FIELD_COLUMN_WIDTHS]) ?? {};
    return {
      columns: columns.map(id => AsmSettingsService.toVisibleColumn(String(id), hiddenIds, widths)),
      sort: AsmSettingsService.toSort(entry),
    };
  }

  /** A column the row gives no width sizes itself, so it carries no `width` key rather than an undefined one. */
  private static toVisibleColumn(id: string, hiddenIds: Set<string>, widths: AsmTableColumnWidths): SolVisibleColumn {
    const column = { id, visible: !hiddenIds.has(id) };
    return Object.hasOwn(widths, id) ? { ...column, width: widths[id] } : column;
  }

  /** A row naming no sort column keeps the table's own default; a direction without one cannot mean anything. */
  private static toSort(entry: AsmSettingPayloadRow): SolTableSort | undefined {
    const columnId = entry[AsmSettingsService.FIELD_SORT_COLUMN];
    const direction = entry[AsmSettingsService.FIELD_SORT_DIRECTION];
    if (typeof columnId !== 'string' || (direction !== 'asc' && direction !== 'desc')) {
      return undefined;
    }
    return { columnId, direction };
  }
}
